import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { toast } from 'sonner'

import { createPublicTenant } from '../public'
import { queryKeys, queryOptions } from '../react-query-config'
import {
  activateTenant,
  createQSEEnterpriseTenant,
  getMyTenantDetails,
  getTenantById,
  getTenantsByReseller,
  getUserTenants,
  permanentDeleteTenant,
  suspendTenant,
  updateTenant,
  updateTenantPlan,
} from '../tenant'
import type {
  CreateQSEEnterpriseTenantDto,
  CreateTenantPublicDto,
  GetUserTenantsResponse,
  PermanentDeleteTenantParams,
  TenantFilters,
  UpdateTenantDto,
  UpdateTenantPlanDto,
} from '../types'
import { useCurrentUser } from './use-auth'

// Get user tenants hook
export const useGetTenants = (
  resellerId?: string,
  filters?: TenantFilters,
  options?: { enabled?: boolean },
): UseQueryResult<GetUserTenantsResponse, Error> => {
  const { enabled, ...restOptions } = options || {}

  return useQuery<GetUserTenantsResponse>({
    queryKey: queryKeys.tenants.userTenants(resellerId, filters),
    queryFn: () => getUserTenants(resellerId, filters),
    ...queryOptions.tenants,
    enabled: enabled !== false, // Default to true if not specified
    ...restOptions,
  })
}

// Get tenants by reseller
export const useTenantsByReseller = (resellerId: string) => {
  return useQuery({
    queryKey: queryKeys.tenants.byReseller(resellerId),
    queryFn: () => getTenantsByReseller(resellerId),
    ...queryOptions.tenants,
    enabled: !!resellerId,
  })
}

// Get tenant by ID
export const useTenant = (id: string) => {
  return useQuery({
    queryKey: queryKeys.tenants.detail(id),
    queryFn: () => getTenantById(id),
    ...queryOptions.tenants,
    enabled: !!id,
  })
}

// Get my tenant details (for authenticated tenant admin)
export const useMyTenantDetails = (options?: { enabled?: boolean }) => {
  const { enabled, ...restOptions } = options || {}
  const { data: currentUser } = useCurrentUser()

  return useQuery({
    queryKey: queryKeys.tenants.myDetails(),
    queryFn: () => getMyTenantDetails(),
    enabled: !!currentUser && currentUser.tenant?.status !== 'incomplete' && enabled !== false,
    ...queryOptions.tenants,
    ...restOptions,
  })
}

// Create tenant
export const usePublicCreateTenant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTenantPublicDto) => createPublicTenant(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenants.lists() })
    },
    onError: (error: any) => {
      toast.error('Error creating tenant', {
        description: error?.response?.data?.error?.details?.message || error.message,
      })
    },
  })
}

// Create QSE Enterprise tenant
export const useCreateQSEEnterpriseTenant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateQSEEnterpriseTenantDto) => createQSEEnterpriseTenant(data),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenants.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.tenants.userTenants() })
      toast.success(`${data.name} tenant created successfully`)
    },
    onError: (error: any) => {
      toast.error('Error creating enterprise tenant', {
        description: error?.response?.data?.error?.details?.message || error.message,
      })
    },
  })
}

// Update tenant plan
export const useUpdateTenantPlan = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, planData }: { id: string; planData: UpdateTenantPlanDto }) =>
      updateTenantPlan(id, planData),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenants.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.tenants.detail(data.id) })
      queryClient.invalidateQueries({ queryKey: queryKeys.tenants.userTenants() })
      toast.success('Tenant plan updated successfully')
    },
    onError: (error: any) => {
      toast.error('Error updating tenant plan', {
        description: error?.response?.data?.error?.details?.message || error.message,
      })
    },
  })
}

// Update tenant
export const useUpdateTenant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, tenantData }: { id: string; tenantData: UpdateTenantDto }) =>
      updateTenant(id, tenantData),
    onSuccess: () => {
      // Invalidate all tenant queries to ensure all filter combinations get fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.tenants.all })

      toast.success('Tenant updated successfully')
    },
    onError: (error: any) => {
      toast.error('Error updating tenant', {
        description: error?.response?.data?.error?.details?.message || error.message,
      })
    },
  })
}

// Suspend tenant
export const useSuspendTenant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => suspendTenant(id),
    onSuccess: () => {
      // Invalidate all tenant queries to ensure all filter combinations get fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.tenants.all })
      toast.success('Tenant suspended successfully')
    },
    onError: (error: any) => {
      toast.error('Error suspending tenant', {
        description: error?.response?.data?.error?.details?.message || error.message,
      })
    },
  })
}

// Activate tenant
export const useActivateTenant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => activateTenant(id),
    onSuccess: () => {
      // Invalidate all tenant queries to ensure all filter combinations get fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.tenants.all })

      toast.success('Tenant activated successfully')
    },
    onError: (error: any) => {
      toast.error('Error activating tenant', {
        description: error?.response?.data?.error?.details?.message || error.message,
      })
    },
  })
}

// Permanently delete tenant
export const usePermanentDeleteTenant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: PermanentDeleteTenantParams) => permanentDeleteTenant(params),
    onSuccess: data => {
      // Invalidate all tenant queries to ensure all filter combinations get fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.tenants.all })

      toast.success(`${data.tenantName} tenant permanently deleted successfully`)
    },
    onError: (error: any) => {
      toast.error('Error deleting tenant', {
        description: error?.response?.data?.error?.details?.message || error.message,
      })
    },
  })
}
